import React, {useState, useEffect, useRef} from 'react';
import {
  formatCentsForInput,
  parseCurrencyInputToCents,
} from 'utils/currencyUtils';

interface CurrencyInputProps {
  id: string;
  label: string;
  value?: number | null; // Value in cents
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string | undefined;
  optional?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  required = false,
  className = '',
  error,
  optional,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined && value !== 0) {
        setDisplayValue(formatCentsForInput(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  const stripLeadingZeros = (s: string) => {
    const trimmed = s.replace(/^0+(?=\d)/, '');
    return trimmed === '' ? '0' : trimmed;
  };

  const formatATMValue = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined) {
      return '';
    }

    const dollars = cents / 100;
    const formatted = dollars.toFixed(2);
    return formatted;
  };

  const handleATMInput = (numericValueRaw: string): string => {
    if (!numericValueRaw) return '';

    const numericValue = stripLeadingZeros(numericValueRaw);

    const limitedValue = numericValue.slice(0, 8);

    if (limitedValue.length <= 2) {
      const cents = parseInt(limitedValue || '0', 10);
      return (cents / 100).toFixed(2);
    } else {
      const dollars = limitedValue.slice(0, -2);
      const cents = limitedValue.slice(-2);
      const dollarsClean = dollars.replace(/^0+(?=\d)/, '');
      return `${dollarsClean}.${cents}`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setDisplayValue('');
      onChange(undefined);
      return;
    }

    const digitsRaw = inputValue.replace(/[^0-9]/g, '');
    if (!digitsRaw) {
      setDisplayValue('');
      onChange(undefined);
      return;
    }

    const formatted = handleATMInput(digitsRaw);
    setDisplayValue(formatted);
    onChange(parseCurrencyInputToCents(formatted));
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== null && value !== undefined && value !== 0) {
      setDisplayValue((value / 100).toFixed(2));
    } else {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (value !== null && value !== undefined && value !== 0) {
      setDisplayValue(formatATMValue(value));
    } else {
      setDisplayValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
    ];

    if (allowedKeys.includes(e.key)) {
      return;
    }
    e.preventDefault();
  };

  const handleClick = () => {
    if (inputRef.current) {
      const input = inputRef.current;
      setTimeout(() => {
        input.setSelectionRange(input.value.length, input.value.length);
      }, 0);
    }
  };

  return (
    <div className={`flex flex-col items-start gap-[0.625rem] ${className}`}>
      <div className="flex justify-between w-full">
        <label
          htmlFor={id}
          className="block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {optional && <span className="text-gray-400 text-sm">{optional}</span>}
      </div>

      <div className="relative w-full">
        <div className="absolute inset-y-0 left-0 pl-3 flex text-gray-400 items-center pointer-events-none">
          $
        </div>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-describedby={`${id}-error`}
          className={`w-full py-2.5 pl-10 pr-3 block focus:outline-cyan-400 border text-gray-500 rounded-lg text-sm font-light placeholder:text-gray-400
            disabled:bg-slate-100 disabled:text-slate-300 disabled:border-0 disabled:pointer-events-none disabled:cursor-not-allowed
            dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60
            dark:focus:ring-neutral-600 ${error !== undefined ? 'border-red-500' : 'border-slate-200'}`}
        />
      </div>

      {error && (
        <p className="text-xs text-red-600" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default CurrencyInput;
